import React from 'react';
// ------------------------------------------------------------------
// MUI Imports as requested by the user
import { Box, Grid, Typography, Button } from '@mui/material'; 
// Import Icons for the capabilities section
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
// ------------------------------------------------------------------
import { useT } from "contexts/TranslationContext";

// Custom color derived from the image for precise matching
const PRIMARY_GREEN = '#34986C'; 

// --- Capability Card Component ---
const CapabilityCard = ({ Icon, title, description }) => {
    return (
        <Box 
            textAlign="center" 
            p={2} 
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
            {/* Icon Circle Container */}
            <Box sx={{
                width: 72, 
                height: 72, 
                borderRadius: '50%', 
                bgcolor: 'grey.100', // Light gray background
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 3, // Margin bottom
                border: '1px solid',
                borderColor: 'grey.300', // Light border as seen in the image
            }}>
                <Icon sx={{ color: 'grey.800', fontSize: 32 }} />
            </Box>
            
            {/* Title */}
            <Typography 
                variant="h6" 
                component="h3"
                sx={{ 
                    fontWeight: 700, 
                    color: 'grey.900', 
                    mb: 1, 
                    lineHeight: 1.2 
                }}
            >
                {title}
            </Typography>
            
            {/* Description */}
            <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ color: 'grey.700', flexGrow: 1 }}
            >
                {description}
            </Typography>
        </Box>
    );
};

// --- Capabilities Section Component ---
const CapabilitiesSection = () => {
  const t = useT();
  // Data structure for the capability cards
  const capabilities = [
    {
      Icon: StarBorderIcon,
      title: t("features.timeTracking"),
      description: t("features.timeTrackingDesc"),
    },
    {
      Icon: ChatBubbleOutlineIcon,
      title: t("features.customPricing"),
      description: t("features.customPricingDesc"),
    },
    {
      Icon: AssessmentOutlinedIcon,
      title: t("features.detailedReporting"),
      description: t("features.detailedReportingDesc"),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        bgcolor: 'white',
        pt: { xs: 8, md: 12 },
        pb: { xs: 10, md: 16 },
        px: { xs: 4, sm: 8, lg: 16 },
        width: '100%',
        maxWidth: 'lg',
        mx: 'auto'
      }}
    >
      {/* Main Title */}
      <Typography
        id="features"
        variant="h4"
        component="h2"
        textAlign="center"
        sx={{
          fontWeight: 800,
          color: 'grey.900',
          mb: 3,
          fontSize: { xs: '2rem', sm: '2.5rem' }
        }}
      >
        {t("features.title")}
      </Typography>

      {/* Subtitle/Intro Text */}
      <Typography
        variant="body1"
        textAlign="center"
        sx={{
          color: 'grey.700',
          maxWidth: 700,
          mx: 'auto',
          mb: { xs: 8, md: 12 }
        }}
      >
        {t("features.subtitle")}
      </Typography>

      {/* 3-Column Grid of Capabilities */}
      <Grid container spacing={{ xs: 6, md: 8 }} justifyContent="center">
        {capabilities.map((capability, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <CapabilityCard
              Icon={capability.Icon}
              title={capability.title}
              description={capability.description}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CapabilitiesSection;